import { useEffect } from "react";
import useLightbox from "../components/utils/useLightbox";
import PageTransition from "../components/PageTransition";
import heroImage from "../assets/ft-hero.png";
import learningBasket from "../assets/learning-basket.png";
import basket from "../assets/basket.png";
import customBasket from "../assets/cart-custom-basket.png"
import subscriptionSetup from "../assets/subscription-setup.png";
import subscriptionCalendar from "../assets/subscription-confirmation.png";
import subscriptionConfirmation from "../assets/subscription-calendar.png";
import subscriptionManagement from "../assets/subscription-management.png";
import customerHome from "../assets/Home with banner.png";
import customerItemList from "../assets/item list.png";
import customerProduct from "../assets/single item.png";
import customerCart from "../assets/cart.png";
import dashboardOverview from "../assets/Dashboard-overview.png";
import skuListing from "../assets/sku-listing.png";
import addNewProduct from "../assets/add-new-product.png";
import tablePricing from "../assets/table-pricing.png";
import homepageCustomization from "../assets/homepage-customization.png";
import pickingOrderList from "../assets/picking-order-list.png";
import pickingOrderDetails from "../assets/picking-order-details.png";
import pickingItemDetails from "../assets/picking-item-details.png";

const GroceryCase = () => {
  const { openLightbox, Lightbox } = useLightbox();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { number: "₹50L", label: "Peak Monthly Turnover" },
    { number: "5K", label: "Customers" },
    { number: "10K", label: "Installations" },
    { number: "3 Yrs", label: "Business Operated" },
  ];

  const timelineData = [
    {
      date: "Aug - Sep 2019",
      title: "Discovery & Planning",
      description:
        "Identified an opportunity in a tier-2 market by speaking with customers about their grocery-buying habits, available choices and what would make shopping easier. These conversations informed the initial product direction.",
    },
    {
      date: "Sep - Dec 2019",
      title: "Build & MVP",
      description:
        "Kept the first release deliberately focused on a customer app and one central operations dashboard. The MVP went live in roughly two months; inventory, pricing and fulfilment capabilities were expanded as the business learned from real operations.",
    },
    {
      date: "Dec 2019",
      title: "Soft Launch",
      description:
        "Launched in one tier-2 city with a focused product. The team could now learn from actual customer behaviour and operational demands instead of trying to build the complete system upfront.",
    },
    {
      date: "Mar 2020",
      title: "Major UI Redesign",
      description:
        "Redesigned the core experience after three months of real-world usage, simplifying critical flows and addressing the biggest usability problems uncovered after launch.",
    },
    {
      date: "Jun 2020",
      title: "Major Feature Release",
      description:
        "Launched Smart Baskets, redesigned delivery-slot selection and introduced bulk pricing. Smart Baskets became one of the product's signature customer experiences.",
    },
    {
      date: "Jun / Jul 2020",
      title: "Hit ₹50L/month 🎯",
      description:
        "Seven months after launch, the business reached peak monthly turnover of ₹50 lakhs and continued operating for the next two years.",
    },
    {
      date: "Sep 2020",
      title: "Subscription Enhancements",
      description:
        "Expanded subscriptions with vacation mode, custom weekday selection and quantity variations, making recurring grocery orders flexible enough for real household routines.",
    },
    {
      date: "2020 - 2022",
      title: "Continuous Improvements",
      description:
        "Continued evolving the customer experience and central operations dashboard through customer feedback, business needs and day-to-day operational learning. A dedicated Picking App supported order fulfilment; delivery-partner management remained a postponed capability.",
    },
    {
      date: "Dec 2022",
      title: "Closure",
      description:
        "After three years of operation, the business closed because we could not secure the funding required for the next stage of expansion. The experience provided valuable lessons in product-market demand, operational economics and the realities of scaling.",
    },
  ];

  const features = [
    {
      title: "Smart Baskets",
      problem:
        "Customers faced a large product catalogue and had to repeatedly build similar grocery baskets from scratch.",
      solution:
        'We introduced pre-built baskets around common household needs, then allowed customers to customize them. Over time, the system learned from previous purchases and made the basket increasingly personal.',
      why:
        "The experience reduced the amount of repetitive decision-making while keeping the customer in control.",
      impact: "60% adoption • 25% higher AOV",
    },
    {
      title: "Daily Subscriptions",
      problem:
        'Customers do not think in terms of "subscriptions." They think in terms of routines — milk every morning, groceries on certain days, different quantities throughout the week.',
      solution:
        "We designed a calendar-based experience where customers could choose days, quantities and vacation periods.",
      why:
        "The experience made recurring ordering feel like managing a routine rather than managing a subscription.",
      impact: "Customer routine + repeat business",
    },
    {
      title: "Slot-Based Delivery",
      problem:
        "Traditional local delivery provided little certainty around when an order would arrive.",
      solution:
        "Customers could choose available delivery slots instead of receiving an open-ended delivery window.",
      why:
        "The model created a clearer customer promise while allowing the operations team to plan delivery capacity.",
      impact: "Better delivery predictability • Strong customer preference",
    },
  ];

  const lessons = [
    "Ship fast, learn faster: We launched a focused MVP in roughly two months, then expanded the product based on real usage and operational needs.",
    "Users don't necessarily want more flexibility — they want smarter defaults. The best experience reduces repetitive decisions while preserving control.",
    "Subscriptions can become part of a customer's routine when the experience reflects how people actually live rather than how software categorizes them.",
    "Validate demand before building complexity. Not every technically interesting feature deserves to be built before the underlying demand is proven.",
    "Unit economics shape UX decisions. A good product experience has to work within the operational realities of the business.",
    "Iteration beats assumption. Smart Baskets evolved through multiple versions before we found an experience that customers adopted.",
    "Design for operations, not just customers. The customer journey depended on connected workflows for picking, inventory, catalogue management, pricing and business operations.",
    "A successful product does not automatically guarantee a successful startup. We proved the model, but funding constraints ultimately limited our ability to scale.",
  ];


  return (
    <div>
      <PageTransition>

        {/* =====================================================
            NAVIGATION
        ====================================================== */}
        <nav
          style={{
            position: "fixed",
            top: 0,
            width: "100%",
            background: "var(--white)",
            borderBottom: "3px solid var(--black)",
            zIndex: 1000,
            padding: "1.5rem 5%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <a
            href="/"
            style={{
              fontWeight: 700,
              color: "var(--black)",
              textDecoration: "none",
              fontSize: "1.1rem",
              transition: "color 0.3s",
            }}
          >
            ← Back to Home
          </a>

          <div
            style={{
              fontWeight: 900,
              fontSize: "1.2rem",
            }}
          >
            Case Study
          </div>
        </nav>


        {/* =====================================================
            HERO
        ====================================================== */}
        <section
          style={{
            marginTop: "5rem",
            background: "var(--black)",
            color: "var(--white)",
            padding: "6rem 8rem",
            position: "relative",
            zIndex: "1",
          }}
        >
          <div className="hero-label">
            Case Study 01
          </div>

          <h1
            style={{
              fontSize: "clamp(3rem, 6vw, 5rem)",
              fontWeight: 900,
              lineHeight: 1,
              marginBottom: "2rem",
              letterSpacing: "-2px",
            }}
          >
            Grocery E-Commerce Platform
          </h1>

          <div className="hero-result">
            0 → ₹50L/month in 7 months
          </div>

          <div className="hero-meta">

            <div>
              <div className="meta-label">
                Role
              </div>

              <div className="meta-value">
                Co-founder · Product & Design
              </div>
            </div>

            <div>
              <div className="meta-label">
                Duration
              </div>

              <div className="meta-value">
                Aug 2019 - Dec 2022
              </div>
            </div>

            <div>
              <div className="meta-label">
                Team
              </div>

              <div className="meta-value">
                Design & Engineering
              </div>
            </div>

            <div>
              <div className="meta-label">
                Ownership
              </div>

              <div className="meta-value">
                Product Strategy · UX · Product Delivery
              </div>
            </div>

          </div>
        </section>


        {/* =====================================================
            HERO IMAGE
        ====================================================== */}
        <section className="hero-image">
  <div
    className="image-placeholder"
    style={{
      width: "100%",
      height: "auto",
      padding: 0,
      border: "none",
      background: "transparent",
      overflow: "hidden",
    }}
  >
    <img
      src={heroImage}
      alt="FreshTrug grocery platform"
      style={{
        display: "block",
        width: "100%",
        maxWidth: "100%",
        height: "auto",
        objectFit: "contain",
      }}
    />
  </div>
</section>


        {/* =====================================================
            CONTENT
        ====================================================== */}
        <div className="content">


          {/* =====================================================
              MY ROLE
          ====================================================== */}
          <section className="section">

            <h2 className="section-title">
              My Role
            </h2>

            <p className="section-text">
              <strong>
                I didn't join FreshTrug. I started it.
              </strong>
            </p>

            <p className="section-text">
              As co-founder working across product and design, I
              helped shape FreshTrug from the initial opportunity
              through product direction, experience design and launch.
              I translated customer and business needs into the
              workflows the product needed to support.
            </p>

            <p className="section-text">
              My design work covered the customer app, the central
              operations dashboard and a dedicated Picking App. This
              included customer shopping journeys as well as internal
              workflows for catalogue, inventory, pricing, wallet
              activity and order picking.
            </p>

            <div
              className="deep-dive"
              style={{
                marginTop: "2rem",
              }}
            >
              <h3>
                Areas of ownership
              </h3>

              <p>
                Product strategy · UX architecture · Interaction design ·
                Design systems · Connected workflows · Product delivery
              </p>
            </div>

          </section>


          {/* =====================================================
              THE PROBLEM
          ====================================================== */}
          <section className="section">

            <h2 className="section-title">
              The Problem
            </h2>

            <p className="section-text">
              I saw an opportunity in a tier-2 market where customers
              had limited grocery choice compared with larger urban
              markets. Rather than assuming that a conventional
              catalogue-led shopping app would solve the problem, I
              spoke with customers about their buying patterns and
              what would make grocery shopping easier.

              One recurring constraint was access to a wider product
              range without the limitations of a small neighbourhood
              store. A warehouse-led model offered a way to carry more
              choice without depending on expensive retail storefronts.
            </p>

            <p className="section-text">
              The business model also created an operational design
              challenge. A wider catalogue and warehouse-led operation
              needed practical ways to maintain products, update prices
              when stock costs changed, manage inventory and process
              customer orders efficiently.
            </p>

            <p className="section-text">
              The product therefore had two connected goals: make
              shopping easier for customers and build the operational
              tools needed to run the business efficiently. I treated
              automation and workflow clarity as part of the product,
              not as back-office details.
            </p>

            <p
              className="section-text"
              style={{
                background: "var(--black)",
                color: "var(--white)",
                padding: "2rem",
                marginTop: "2rem",
                fontSize: "1.3rem",
                fontWeight: 700,
              }}
            >
              "The challenge wasn't only helping people buy groceries.
              It was connecting the entire journey — from product
              discovery to fulfilment and delivery."
            </p>

          </section>


          {/* =====================================================
              PRODUCT STRATEGY & PRIORITISATION
          ====================================================== */}
          <section className="section">

            <h2 className="section-title">
              Start Focused. Expand Around Real Needs.
            </h2>

            <p className="section-text">
              I did not want to delay learning while trying to build
              every operational capability upfront. The first MVP
              focused on two essentials: a customer app to place
              orders and a central dashboard to manage the business.
              We launched in roughly two months.
            </p>

            <p className="section-text">
              Once orders began flowing through the business, the
              product needed more specialised workflows. Inventory
              and pricing controls became important as incoming stock
              costs changed. Order processing needed a dedicated
              Picking App so a picker could take responsibility for
              the task, while broader management and reporting stayed
              in the central dashboard.
            </p>

            <p className="section-text">
              Some capabilities were intentionally deferred. Delivery
              partner management was postponed rather than included
              in the initial scope. Subscriptions were also not part
              of the first release; they were introduced later as the
              product evolved.
            </p>

            <div className="deep-dive" style={{ marginTop: "2rem" }}>
              <h3>What this prioritisation enabled</h3>
              <p>
                Launch with the minimum experience needed to serve
                customers, learn from real operations, then invest in
                the workflows that addressed the next constraints.
              </p>
            </div>

          </section>


          {/* =====================================================
              STATS
          ====================================================== */}
          <div className="stats-showcase">

            {stats.map((stat, index) => (
              <div
                key={index}
                className="stat-big"
              >
                <div className="number">
                  {stat.number}
                </div>

                <div className="label">
                  {stat.label}
                </div>
              </div>
            ))}

          </div>


          {/* =====================================================
              JOURNEY
          ====================================================== */}
          <section className="section">

            <h2 className="section-title">
              The Journey
            </h2>

            <div className="timeline">

              {timelineData.map((item, index) => (
                <div
                  key={index}
                  className="timeline-item"
                >
                  <div className="timeline-date">
                    {item.date}
                  </div>

                  <div className="timeline-content">

                    <h3>
                      {item.title}
                    </h3>

                    <p>
                      {item.description}
                    </p>

                  </div>
                </div>
              ))}

            </div>

          </section>


          {/* =====================================================
              KEY FEATURES
          ====================================================== */}
          <section className="section">

            <h2 className="section-title">
              Key Features
            </h2>

            <p className="section-text">
              FreshTrug brought together customer-facing experiences
              and the tools needed to run the service. Smart Baskets
              became the flagship customer experience, while
              subscriptions connected repeat customer routines with
              more predictable business demand.
            </p>

            <div className="features-grid">

              {features.map((feature, index) => (
                <div
                  key={index}
                  className="feature-card"
                >

                  <h3>
                    {feature.title}
                  </h3>

                  <p>
                    <strong>
                      The Problem:
                    </strong>{" "}
                    {feature.problem}
                  </p>

                  <p>
                    <strong>
                      Our Solution:
                    </strong>{" "}
                    {feature.solution}
                  </p>

                  {feature.why && (
                    <p>
                      <strong>
                        Why It Worked:
                      </strong>{" "}
                      {feature.why}
                    </p>
                  )}

                  <div className="feature-impact">
                    {feature.impact}
                  </div>

                </div>
              ))}

            </div>

          </section>


          {/* =====================================================
              FEATURE VISUALS
          ====================================================== */}

{/* DAILY SUBSCRIPTIONS */}
<section className="section">
  <h2 className="section-title">
    Daily Subscriptions — From Setup to Management
  </h2>

  <p className="section-text">
    A calendar-based subscription experience that lets customers
    choose products, delivery days and recurring quantities,
    while retaining control over their active subscriptions.
  </p>

  <div className="image-grid subscription-grid">
    {[
      {
        image: subscriptionSetup,
        title: "Product Selection",
        alt: "FreshTrug subscription product selection",
      },
      {
        image: subscriptionCalendar,
        title: "Subscription Configuration",
        alt: "FreshTrug subscription frequency and delivery day selection",
      },
      {
        image: subscriptionConfirmation,
        title: "Review & Confirmation",
        alt: "FreshTrug subscription review and confirmation",
      },
      {
        image: subscriptionManagement,
        title: "Subscription Management",
        alt: "FreshTrug active subscription management",
      },
    ].map((item) => (
      <div
        className="image-grid-item"
        key={item.title}
        onClick={() => openLightbox(item.image)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openLightbox(item.image);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Open ${item.title} image`}
        style={{ cursor: "zoom-in" }}
      >
        <img src={item.image} alt={item.alt} />
        <p>{item.title}</p>
      </div>
    ))}
  </div>
</section>


          {/* =====================================================
              DESIGN DEEP DIVE
          ====================================================== */}
          <section className="section">

            <h2 className="section-title">
              Design Deep-Dive: Smart Baskets
            </h2>

            <p className="section-text">
              As the product matured, the work extended beyond
              the customer app into the central operations
              dashboard and a dedicated Picking App. The product
              connected shopping, subscriptions, inventory,
              pricing, wallet workflows and order fulfilment.
            </p>

            <p className="section-text">
              <strong>
                Smart Baskets became one of the product's
                signature experiences.
              </strong>{" "}
              It was a good example of how we used real customer
              behavior to evolve an idea through multiple
              iterations.
            </p>


            {/* Challenge */}
            <div className="challenge-block">

              <h3>
                The Challenge
              </h3>

              <p>
                <strong>
                  The Data:
                </strong>{" "}
                Customers had to navigate a broad grocery
                catalogue and repeatedly rebuild baskets containing
                familiar household products.
              </p>

              <p>
                <strong>
                  The User Problem:
                </strong>{" "}
                Customers didn't necessarily want more choice.
                They wanted a faster way to buy the products
                they already knew they needed.
              </p>

              <p>
                <strong>
                  The Hypothesis:
                </strong>{" "}
                What if we built a useful starting basket for
                customers and then allowed the experience to
                become more personalized over time?
              </p>

            </div>


            {/* Iteration 1 */}
            <div className="innovation-block">

              <h3>
                Iteration 1: Static Baskets — Failed
              </h3>

              <p>
                <strong>
                  What We Built:
                </strong>{" "}
                Predefined baskets such as "Bachelor Pack" and
                "Family of 4" with fixed products and quantities.
              </p>

              <p>
                <strong>
                  What Happened:
                </strong>{" "}
                Adoption was only 15%. Customers wanted
                convenience, but the fixed contents made the
                experience too rigid.
              </p>

              <p>
                <strong>
                  The Learning:
                </strong>{" "}
                People want convenience, not constraint.
              </p>

            </div>


           <div className="image-full-basket">
  <img
    src={basket}
    alt="FreshTrug fixed basket experience"
    onClick={() => openLightbox(basket)}
    style={{
      display: "block",
      width: "100%",
      maxWidth: "100%",
      height: "auto",
      objectFit: "contain",
      cursor: "zoom-in",
    }}
  />
  <p>Iteration 1 — Fixed Baskets · 15% adoption</p>
</div>


            {/* Iteration 2 */}
            <div className="challenge-block">

              <h3>
                Iteration 2: Customizable Baskets — Better
              </h3>

              <p>
                <strong>
                  What We Changed:
                </strong>{" "}
                Customers could add a predefined basket to the
                cart and customize quantities or remove products
                before checkout.
              </p>

              <p>
                <strong>
                  What Happened:
                </strong>{" "}
                Adoption improved to 35%, but customers were
                still repeatedly editing the same basket.
              </p>

              <p>
                <strong>
                  The Insight:
                </strong>{" "}
                We were solving the wrong problem. Customers
                didn't need one perfect basket — they needed
                the product to learn what their basket looked like.
              </p>

            </div>


          <div className="image-full-basket">
  <img
    src={customBasket}
    alt="FreshTrug customisable basket experience"
    onClick={() => openLightbox(customBasket)}
    style={{
      display: "block",
      width: "100%",
      maxWidth: "100%",
      height: "auto",
      objectFit: "contain",
      cursor: "zoom-in",
    }}
  />
  <p>Iteration 2 — Customisable Baskets · 35% adoption</p>
</div>


            {/* Iteration 3 */}
            <div className="innovation-block">

              <h3>
                Iteration 3: Learning Baskets — Winner 🎯
              </h3>

              <p>
                <strong>
                  The Breakthrough:
                </strong>{" "}
                What if the basket remembered?
              </p>

              <p>
                <strong>
                  How It Worked:
                </strong>
              </p>

              <ul>

                <li>
                  First order: the customer selected a basket
                  and customized it.
                </li>

                <li>
                  The system learned their product and quantity
                  preferences.
                </li>

                <li>
                  The next order started with those preferences
                  already applied.
                </li>

                <li>
                  Over time, the basket became the customer's
                  basket rather than a generic predefined basket.
                </li>

              </ul>

              <p
                style={{
                  marginTop: "1.5rem",
                }}
              >
                <strong>
                  The principle:
                </strong>{" "}
                Automate the repetitive parts. Keep the customer
                in control.
              </p>

              <p>
                <strong>
                  Results:
                </strong>
              </p>

              <ul>
                <li>
                  60% adoption
                </li>

                <li>
                  25% higher average order value
                </li>

                <li>
                  Cart abandonment improved from 65% to 40%
                </li>

                <li>
                  Basket users showed higher retention
                </li>
              </ul>

            </div>


         <div className="image-full-basket">
  <img
    src={learningBasket}
    alt="FreshTrug learning basket experience"
    onClick={() => openLightbox(learningBasket)}
    style={{
      display: "block",
      width: "100%",
      maxWidth: "100%",
      height: "auto",
      objectFit: "contain",
      cursor: "zoom-in",
    }}
  />
  <p>Iteration 3 — Learning Baskets · 60% adoption</p>
</div>


            {/* Design Decisions */}
            <div className="deep-dive">

              <h3>
                Design Decisions That Mattered
              </h3>

              <p>
                <strong>
                  1. Edit in Cart:
                </strong>{" "}
                We chose to let customers modify the basket after
                adding it to the cart so they could make decisions
                with the full order context visible.
              </p>

              <p>
                <strong>
                  2. Visual Feedback:
                </strong>{" "}
                We used clear feedback to communicate when a basket
                had been customized based on previous behavior.
              </p>

              <p>
                <strong>
                  3. Progressive Disclosure:
                </strong>{" "}
                First-time customers could understand the complete
                experience, while returning customers could move
                through a more streamlined flow.
              </p>

              <p>
                <strong>
                  4. Product + Engineering Trade-off:
                </strong>{" "}
                The experience required more sophisticated logic
                than a static basket. I pushed for the more useful
                product behavior rather than stopping at the
                simplest implementation.
              </p>

            </div>

          </section>


          {/* =====================================================
              COMMERCE & OPERATIONS DESIGN
          ====================================================== */}
          <section className="section">

            <h2 className="section-title">
              Designing the Business Operating System
            </h2>

            <p className="section-text">
              Customer convenience depended on the systems behind
              the storefront. I designed workflows in the central
              dashboard to help the business maintain its catalogue,
              manage stock and pricing, oversee wallet activity and
              keep the operation running.
            </p>

            <div className="features-grid">

              <div className="feature-card">
                <h3>Product & Catalogue Management</h3>
                <p>
                  Workflows for managing products, brands and
                  sub-brands, including creating new product entries
                  and maintaining the customer-facing catalogue.
                </p>
              </div>

              <div className="feature-card">
                <h3>Inventory & SKU Management</h3>
                <p>
                  Operational views for maintaining inventory and
                  SKU-level information, including batch and pricing
                  details.
                </p>
              </div>

              <div className="feature-card">
                <h3>Cost-based Pricing</h3>
                <p>
                  A percentage-based price update workflow connected
                  selling prices to incoming inventory costs, reducing
                  the need to manually rework prices product by product.
                </p>
              </div>

              <div className="feature-card">
                <h3>Wallet & Refund Management</h3>
                <p>
                  Refunds for expired or damaged products could be
                  credited directly to a customer's wallet without
                  waiting for the order to return to the warehouse.
                  Each transaction was logged for admin review.
                </p>
              </div>

              <div className="feature-card">
                <h3>Picking App</h3>
                <p>
                  A separate, task-focused app showed pickers their
                  orders and the details needed to process them.
                  Inventory, charts and other management functions
                  remained in the central dashboard.
                </p>
              </div>

              <div className="feature-card">
                <h3>Storefront & Offers</h3>
                <p>
                  Table-based pricing, offers management, homepage
                  customisation and slider management gave the
                  business control over products and promotions
                  shown to customers.
                </p>
              </div>

            </div>

            <p className="section-text" style={{ marginTop: "2rem" }}>
              The principle was to keep business management in one
              place while giving specialised operational roles only
              the interface they needed to complete their work.
            </p>

            <div className="challenge-block" style={{ marginTop: "2rem" }}>
              <h3>Wallet refunds: fast resolution with an audit trail</h3>
              <p>
                When a customer received an expired or damaged product,
                the refund could be credited directly to their wallet
                without waiting for the order to return to the
                warehouse. The transaction was logged so the admin
                team could review refund activity afterwards.
              </p>
              <p>
                This connected a faster customer resolution with
                visibility for the business.
              </p>
            </div>

          </section>


          {/* =====================================================
              THE CONNECTED PRODUCT EXPERIENCE
          ====================================================== */}
          <section className="section">

            <h2 className="section-title">
              One Dashboard. A Focused Picking Experience.
            </h2>

            <p className="section-text">
              FreshTrug did not need a separate dashboard for every
              operational function. The central dashboard brought
              together management tasks such as inventory, pricing,
              wallet activity, business reporting and other controls.
            </p>

            <p className="section-text">
              Order picking was different. It was a repetitive,
              time-sensitive task that needed a simple interface and
              clear responsibility. I designed a dedicated Picking
              App that focused on the order list, order search, order
              details and item picking — without exposing unrelated
              management tools.
            </p>

            <div className="deep-dive" style={{ marginTop: "2rem" }}>
              <h3>Separate the task, not the whole system</h3>
              <p>
                The dashboard remained the source for business
                management. The Picking App provided a focused
                execution experience for the person responsible for
                preparing each order.
              </p>
            </div>

            <p className="section-text" style={{ marginTop: "2rem" }}>
              Delivery-partner management was considered but postponed.
              It is intentionally not represented here as a completed
              feature or shipped application.
            </p>

          </section>


          {/* =====================================================
              MULTI-APP VISUALS
          ====================================================== */}
 
{/* CUSTOMER APP */}
<section className="section">
  <h2 className="section-title">
    Customer App — The Shopping Experience
  </h2>

  <p className="section-text">
    From discovering products to managing an order, the customer
    app brought the everyday grocery shopping journey together.
  </p>

  <div className="image-grid customer-app-grid">
    {[
      {
        image: customerHome,
        title: "Home & Product Discovery",
        alt: "FreshTrug customer app home screen",
      },
      {
        image: customerItemList,
        title: "Product Listing",
        alt: "FreshTrug grocery product listing",
      },
      {
        image: customerProduct,
        title: "Product Details",
        alt: "FreshTrug product detail screen",
      },
      {
        image: customerCart,
        title: "Shopping Cart",
        alt: "FreshTrug shopping cart",
      },
    ].map((item) => (
      <div
        className="image-grid-item"
        key={item.title}
        onClick={() => openLightbox(item.image)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openLightbox(item.image);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Open ${item.title} image`}
        style={{ cursor: "zoom-in" }}
      >
        <img src={item.image} alt={item.alt} />
        <p>{item.title}</p>
      </div>
    ))}
  </div>
</section>

{/* CENTRAL DASHBOARD */}
{/* CENTRAL DASHBOARD */}
<section className="section">
  <h2 className="section-title">
    Central Dashboard — Business Operations
  </h2>

  <p className="section-text">
    A central workspace for managing products, inventory, pricing,
    sales performance and customer-facing storefront content.
  </p>

  <div className="image-grid dashboard-grid">
    {[
      {
        image: dashboardOverview,
        title: "Business Overview",
        alt: "FreshTrug dashboard showing sales, current servings and low stock",
      },
      {
        image: skuListing,
        title: "SKU & Stock Management",
        alt: "FreshTrug SKU listing and stock management dashboard",
      },
      {
        image: addNewProduct,
        title: "Product Management",
        alt: "FreshTrug new product creation and configuration",
      },
      {
        image: tablePricing,
        title: "Pricing Management",
        alt: "FreshTrug category, subcategory and brand-based pricing",
      },
      {
        image: homepageCustomization,
        title: "Homepage Customisation",
        alt: "FreshTrug homepage content and category customisation",
      },
    ].map((item) => (
      <div
        className="image-grid-item"
        key={item.title}
        onClick={() => openLightbox(item.image)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openLightbox(item.image);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Open ${item.title} image`}
        style={{ cursor: "zoom-in" }}
      >
        <img src={item.image} alt={item.alt} />
        <p>{item.title}</p>
      </div>
    ))}
  </div>
</section>


{/* PICKING APP */}
<section className="section">
  <h2 className="section-title">
    Picking App — Order Fulfilment
  </h2>

  <p className="section-text">
    A dedicated, task-focused application for warehouse operations.
    Pickers could find assigned orders, review customer and delivery
    details, and mark individual items as picked.
  </p>

  <div className="image-grid picking-app-grid">
    {[
      {
        image: pickingOrderList,
        title: "Order List",
        alt: "FreshTrug picking app order list and picking status",
      },
      {
        image: pickingOrderDetails,
        title: "Order Details",
        alt: "FreshTrug picking app customer and order details",
      },
      {
        image: pickingItemDetails,
        title: "Item Picking",
        alt: "FreshTrug picking app item checklist and fulfilment",
      },
    ].map((item) => (
      <div
        className="image-grid-item"
        key={item.title}
        onClick={() => openLightbox(item.image)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openLightbox(item.image);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Open ${item.title} image`}
        style={{ cursor: "zoom-in" }}
      >
        <img src={item.image} alt={item.alt} />
        <p>{item.title}</p>
      </div>
    ))}
  </div>
</section>



{/* CONNECTED WORKFLOWS */}
<section className="section">
  <h2 className="section-title">
    Connected Workflows
  </h2>

  <p className="section-text">
    Three focused experiences working together to connect
    customer shopping, business management and order fulfilment.
  </p>

  <div className="deep-dive">
    <h3>One connected commerce experience</h3>

    <div className="workflow-visual">
      <div className="workflow-item">
        <strong>Customer App</strong>
        <span>Shopping · Cart · Subscriptions</span>
      </div>

      <div className="workflow-arrow">↕</div>

      <div className="workflow-item">
        <strong>Central Dashboard</strong>
        <span>Inventory · Pricing · Orders · Wallet</span>
      </div>

      <div className="workflow-arrow">↕</div>

      <div className="workflow-item">
        <strong>Picking App</strong>
        <span>Order Search · Details · Item Picking</span>
      </div>
    </div>
  </div>
</section>




          {/* =====================================================
              IMPACT
          ====================================================== */}
          <section className="section">

            <h2 className="section-title">
              Impact & Results
            </h2>

            <p className="section-text">
              FreshTrug operated for three years and grew from an
              initial product idea into a functioning grocery
              business.
            </p>

            <p className="section-text">
              The product reached thousands of customers and
              installations, while the business reached a peak
              monthly turnover of ₹50 lakhs.
            </p>

            <div className="stats-showcase">

              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="stat-big"
                >

                  <div className="number">
                    {stat.number}
                  </div>

                  <div className="label">
                    {stat.label}
                  </div>

                </div>
              ))}

            </div>


            <p
              className="section-text"
              style={{
                marginTop: "3rem",
              }}
            >
              <strong>
                Growth:
              </strong>{" "}
              The business reached ₹50L in peak monthly turnover
              seven months after launch.
            </p>

            <p className="section-text">

              <strong>
                Product adoption:
              </strong>{" "}
              Smart Baskets became one of the product's signature
              features. Reported product metrics included 60% basket
              adoption, 25% higher average order value and cart
              abandonment moving from 65% to 40%. Subscriptions and
              delivery slots also became part of the customer offer.

            </p>

            <p className="section-text">

              <strong>
                Market validation:
              </strong>{" "}
              The business operated in one tier-2 city, serving
              thousands of customers and reaching peak monthly
              turnover of ₹50 lakhs.

            </p>

          </section>


          {/* =====================================================
              LESSONS
          ====================================================== */}
          <div className="lessons">

            <h3>
              What I Learned
            </h3>

            <ul>

              {lessons.map((lesson, index) => (
                <li key={index}>
                  {lesson}
                </li>
              ))}

            </ul>

          </div>

        </div>


        {/* =====================================================
            NEXT PROJECT
        ====================================================== */}
        <section className="next-project">

          <h2>
            Next Case Study
          </h2>

          <p
            style={{
              marginBottom: "3rem",
              fontSize: "1.2rem",
              color: "var(--gray)",
            }}
          >
            See how I approached food delivery economics,
            product strategy and operational complexity.
          </p>

          <a
            href="/food-delivery"
            className="next-btn"
          >
            Food Delivery Platform →
          </a>

        </section>


        {/* =====================================================
            FOOTER
        ====================================================== */}
        <footer>
          <p>
            © 2026 • Built with intention, shipped with pride
          </p>
        </footer>

        <Lightbox />
      </PageTransition>
    </div>
  );
};

export default GroceryCase;